import { Circle } from '@mui/icons-material';
import { Box, Button, Link } from '@mui/material';
import { DataGridProProps, GRID_DETAIL_PANEL_TOGGLE_FIELD, GridRowParams } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { memo, useEffect, useState } from 'react';
import { PermissionsService } from '../../../auth/services/PermissionsService';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { ExpandCell } from '../../../common/components/Cells/ExpandCell';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { AuthorizationError } from '../../../common/errors/errors';
import { withRouter, WithRouterProps } from '../../../common/higher-order-components/with-router';
import { FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { DateUtils } from '../../../common/utils/DateUtils';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { bind } from '../../../common/utils/decorators';
import { EXTERNAL_URLS, PATHS } from '../../../routing/routes';
import { ProgressBarDisplay } from '../components/ProgressBarDisplay';
import { ProgressCountDisplay } from '../components/ProgressCountDisplay';
import { ScansContextType } from '../contexts/ScansContext';
import { getScanStatusColor, getScanStatusLabel, ScanStatus } from '../enums/ScanStatus';
import { Scan } from '../models/Scan';
import { ScansService } from '../services/ScansService';
import { HostList } from './HostList';

type State = PaginatedTableState & {};

type Props = WithRouterProps<ScansContextType> & {};

type StopButtonProps = {
  id: number;
  tabIndex: 0 | -1;
  onClick: (id: number) => Promise<void>;
};

const StopButton = memo(({ id, tabIndex, onClick }: StopButtonProps) => {
  const [disabled, setDisabled] = useState(true);

  useEffect(() => {
    const loadPermission = async () => {
      const permissionsService = new PermissionsService();
      const canUpdateScan = await permissionsService.canUpdateScan(id);
      setDisabled(!canUpdateScan);
    };

    loadPermission();

    return () => {
      setDisabled(true);
    };
  }, [id]);

  return (
    <Button tabIndex={tabIndex} variant="contained" style={{ width: '50%' }} color="error" disabled={disabled} onClick={() => onClick(id)}>
      {t('stop')}
    </Button>
  );
});

class Controller extends PaginatedTableController<Scan, Props, State> {
  private readonly scansService = new ScansService();

  private openScanRowIds = new Set<number>();

  componentWillUnmount() {
    super.componentWillUnmount();
    this.scansService.cancelAllRequests();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    super.componentDidUpdate(prevProps, prevState);

    if (prevProps.context.nonce !== this.props.context.nonce) {
      this.reloadTableContent();
    }
  }

  protected doInitialDataFetch(): void {
    // do nothing until permissions are fetched
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const permission = await this.permissionsService.canAccessOrganizationScans();
    if (!permission) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }
    super.doInitialDataFetch();
  }

  protected shouldStoreFiltersInUrl(): boolean {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [
      {
        filterKey: 'name',
        label: t('name'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
      {
        filterKey: 'status',
        label: t('status'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
        options: [
          { value: ScanStatus.CANCELED, label: t('canceled') },
          { value: ScanStatus.COMPLETED, label: t('completed') },
          { value: ScanStatus.PENDING, label: t('pending') },
          { value: ScanStatus.IN_PROGRESS, label: t('inProgress') },
        ],
      },
      {
        filterKey: 'startedAt',
        label: t('scanStart'),
        type: FilterValueInputType.DATE_TIME,
        operations: [
          { value: Operation.GE, label: t('filterLabels.isAfterOrEqual') },
          { value: Operation.GT, label: t('filterLabels.isAfter') },
          { value: Operation.LE, label: t('filterLabels.isBeforeOrEqual') },
          { value: Operation.LT, label: t('filterLabels.isBefore') },
        ],
      },
      {
        filterKey: 'endedAt',
        label: t('scanEnd'),
        type: FilterValueInputType.DATE_TIME,
        operations: [
          { value: Operation.GE, label: t('filterLabels.isAfterOrEqual') },
          { value: Operation.GT, label: t('filterLabels.isAfter') },
          { value: Operation.LE, label: t('filterLabels.isBeforeOrEqual') },
          { value: Operation.LT, label: t('filterLabels.isBefore') },
        ],
      },
    ];
  }

  protected getFieldConfigurations(): FieldConfiguration<Scan>[] {
    return [
      {
        fieldKey: GRID_DETAIL_PANEL_TOGGLE_FIELD,
        label: '',
        columnProps: { maxWidth: 52, minWidth: 52 },
        render: (item, { tabIndex }) => (
          <ExpandCell
            tabIndex={tabIndex}
            expanded={this.openScanRowIds.has(item.getId())}
            disabled={item.getTotalHosts() === 0}
            onToggle={() => this.toggleOpenScan(item)}
            openLabel={
              t('a11y.organization.scans.expandScanRow', {
                scanId: item.getId(),
                scanName: item.getName(),
              }) as string
            }
            closeLabel={
              t('a11y.organization.scans.collapseScanRow', {
                scanId: item.getId(),
                scanName: item.getName(),
              }) as string
            }
          />
        ),
      },
      {
        fieldKey: 'name',
        sortable: true,
        gqlKeys: { sorterField: 'name' },
        columnProps: { minWidth: 300 },
        label: t('name'),
        description: t('name'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{model.getName()}</TypographyOverflow>,
      },
      {
        fieldKey: 'project',
        label: t('project'),
        description: t('project'),
        render: (model, { tabIndex, hasFocus }) => {
          const project = model.getProject();
          if (!project) {
            return <BlankCell />;
          }
          return (
            <Link
              tabIndex={tabIndex}
              color="text.primary"
              href={UrlHelper.generatePath(PATHS.PROJECT_DASHBOARD, { projectId: project.getId().toString() })}
              aria-label={
                t('a11y.organization.projects.linkToProjectDashboard', {
                  projectId: project.getId(),
                  projectName: project.getName(),
                }) as string
              }
            >
              <TypographyOverflow hasFocus={hasFocus}>{project.getName()}</TypographyOverflow>
            </Link>
          );
        },
      },
      {
        fieldKey: 'status',
        sortable: true,
        gqlKeys: { sorterField: 'status' },
        label: t('status'),
        description: t('status'),
        render: (model, { hasFocus }) => {
          return (
            <>
              <Circle sx={{ fontSize: 12, color: getScanStatusColor(model.getStatus()), mr: 1 }} />
              <TypographyOverflow hasFocus={hasFocus}>{getScanStatusLabel(model.getStatus())}</TypographyOverflow>
            </>
          );
        },
      },
      {
        fieldKey: 'progress',
        label: t('progress'),
        description: t('progress'),
        render: (model) => {
          const successfulHosts = model.getSuccessfulHosts();
          const failedHosts = model.getFailedHosts();
          const completedHosts = successfulHosts + failedHosts;
          const totalHosts = model.getTotalHosts();
          switch (model.getStatus()) {
            case ScanStatus.CANCELED:
            case ScanStatus.COMPLETED:
              return <ProgressCountDisplay successfulHosts={successfulHosts} failedHosts={failedHosts} />;
            case ScanStatus.PENDING:
            case ScanStatus.IN_PROGRESS:
              return <ProgressBarDisplay totalHosts={totalHosts} completedHosts={completedHosts} />;
          }
        },
      },
      {
        fieldKey: 'startedAt',
        sortable: true,
        gqlKeys: { sorterField: 'startedAt' },
        label: t('scanStart'),
        description: t('scanStart'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{DateUtils.toDateTimeString(model.getStartedAt())}</TypographyOverflow>,
      },
      {
        fieldKey: 'endedAt',
        sortable: false,
        label: t('scanEnd'),
        description: t('scanEnd'),
        render: (model, { hasFocus }) => {
          const endedAt = model.getEndedAt();
          return endedAt ? <TypographyOverflow hasFocus={hasFocus}>{DateUtils.toDateTimeString(endedAt)}</TypographyOverflow> : <BlankCell blankText={'-'} />;
        },
      },
      {
        fieldKey: 'duration',
        label: t('duration'),
        description: t('duration'),
        render: (model, { hasFocus }) => {
          const endedAt = model.getEndedAt();
          return endedAt ? (
            <TypographyOverflow hasFocus={hasFocus}>{DateUtils.toDurationString(model.getStartedAt(), endedAt)}</TypographyOverflow>
          ) : (
            <BlankCell blankText={'-'} />
          );
        },
      },
      {
        fieldKey: 'actions',
        label: t('action'),
        description: t('a11y.organization.scans.actions'),
        render: (model, { tabIndex }) => {
          if (![ScanStatus.IN_PROGRESS, ScanStatus.PENDING].includes(model.getStatus())) {
            return <BlankCell blankText={'-'} />;
          }
          return <StopButton tabIndex={tabIndex} id={model.getId()} onClick={this.handleStopScan} />;
        },
      },
    ];
  }

  protected getAdditionalDataGridProps(): Partial<DataGridProProps<Scan>> {
    return {
      ...super.getAdditionalDataGridProps(),

      nonce: this.getNonce(),

      getRowId: (scan) => this.getRowId(scan),

      // Allow the expanded row to display all content
      getDetailPanelHeight: () => 'auto',

      // Control which rows are expanded based on internal state
      detailPanelExpandedRowIds: this.getMappedOpenRowIds(),

      // Render the expanded row
      getDetailPanelContent: this.renderHosts,
    };
  }

  private getMappedOpenRowIds(): string[] {
    const openScans = this.data.filter((scan) => this.openScanRowIds.has(scan.getId()));

    return openScans.map((scan) => this.getRowId(scan));
  }

  private getRowId(scan: Scan): string {
    return [scan.getId(), scan.getStatus(), scan.getTotalHosts(), scan.getFailedHosts(), scan.getSuccessfulHosts()].join('_');
  }

  /**
   * Builds a nonce that should change whenever the inner MemoizedMuiDataGrid needs to be forced to update.
   * Uses the following parameters:
   * - `severities`: the table header and expanded project details must reflect the checkbox selection
   * - `openProjectRowIds`: table rows must expand and collapse, and the project name must change upon edit
   */
  private getNonce(): string {
    return [...this.openScanRowIds].join('_');
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<Scan>> {
    try {
      const response = await this.scansService.getList(cursorInfo, this.getFilters(), this.getSorters());

      // prefetch permissions
      const scanIds = response.data.map((s) => s.getId());
      await this.permissionsService.bulkPrefetchScanPermissions(scanIds);

      return response;
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected renderRightToolbarElements(): React.ReactElement | null {
    const buttonProps = { href: UrlHelper.wrapExternalDocsUrl(EXTERNAL_URLS.DOCS_SCANS_PAGE_TOUR), target: '_blank', 'aria-label': t('scanDocs') };
    return (
      <Button variant="outlined" size={'small'} color="primary" {...buttonProps}>
        {t('scanDocs')}
      </Button>
    );
  }

  private toggleOpenScan(scan: Scan): void {
    if (this.openScanRowIds.has(scan.getId())) {
      this.openScanRowIds.delete(scan.getId());
      this.forceUpdate();
      return;
    }

    this.openScanRowIds.add(scan.getId());
    this.forceUpdate();
  }

  @bind
  private renderHosts({ row: scan }: GridRowParams<Scan>) {
    if (scan.getTotalHosts() === 0) {
      return null;
    }

    return (
      <Box sx={{ p: 2, backgroundColor: 'backdrops.expandedRow' }}>
        <HostList scanId={scan.getId()} />
      </Box>
    );
  }

  @bind
  private async handleStopScan(id: number) {
    try {
      await this.scansService.stopScan(id);

      this.reloadTableContent();
    } catch (e: unknown) {
      this.handleError(e);
    }
  }
}

export const CrowdStrikeScansList = withRouter(Controller);
